import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import PageMeta from "../components/PageMeta";
import { fetchJournalList } from "../lib/api";
import { ArrowUpRight, Rss } from "lucide-react";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

function PostRow({ p }) {
  return (
    <Link
      to={`/journal/${p.slug}`}
      data-testid={`journal-post-${p.id}`}
      className="group block py-10 md:py-12 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 hover:bg-linen-alt/40 transition-colors px-2 md:px-4 -mx-2 md:-mx-4"
    >
      <div className="md:col-span-3">
        <div className="text-xs uppercase tracking-[0.22em] text-ember font-accent font-semibold">
          {p.tag}
        </div>
        <div className="mt-3 text-sm text-ink-muted font-accent">
          {formatDate(p.created_at)}
        </div>
      </div>
      <div className="md:col-span-9">
        <h2 className="font-serif text-2xl md:text-3xl lg:text-[2rem] text-ink leading-tight tracking-tight group-hover:text-river transition-colors text-balance">
          {p.title}
        </h2>
        <p className="mt-4 text-ink-muted leading-relaxed text-base md:text-lg font-light max-w-2xl">
          {p.excerpt}
        </p>
        <div className="mt-5 inline-flex items-center gap-1.5 text-river font-accent text-sm font-semibold group-hover:gap-2.5 transition-all">
          Read the full piece
          <ArrowUpRight size={14} strokeWidth={1.5} />
        </div>
      </div>
    </Link>
  );
}

export default function JournalPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJournalList()
      .then(setPosts)
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  // Discovery link so browsers/readers find the feed
  useEffect(() => {
    const link = document.createElement("link");
    link.rel = "alternate";
    link.type = "application/rss+xml";
    link.title = "You Are Not Alone — Journal";
    link.href = "/api/journal.xml";
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, []);

  return (
    <div data-testid="journal-page">
      <PageMeta
        title="Journal"
        description="Writing about the child protection system in England and Wales — calmly, in plain English. Occasional pieces on the law, the numbers, and what families tell us."
        path="/journal"
      />
      <PageHeader
        overline="Journal"
        title="Writing about the system, calmly."
        lede="Occasional pieces — on the law, on the numbers, on what families tell us. We write slowly and we try not to shout."
      />

      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-20">
        <div className="flex items-center justify-end mb-8">
          <a
            href="/api/journal.xml"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="journal-rss-link"
            className="inline-flex items-center gap-2 text-sm font-accent text-ink-muted hover:text-river transition-colors"
            title="Subscribe with any RSS reader"
          >
            <Rss size={14} strokeWidth={1.5} />
            RSS feed
          </a>
        </div>
        {loading ? (
          <p data-testid="journal-loading" className="text-ink-muted font-accent">Loading…</p>
        ) : posts.length === 0 ? (
          <div className="border border-rule bg-linen-alt p-10 text-center">
            <p className="text-ink-muted">
              No pieces have been published yet. Come back soon.
            </p>
          </div>
        ) : (
          <div
            className="divide-y divide-rule border-y border-rule"
            data-testid="journal-list"
          >
            {posts.map((p) => (
              <PostRow key={p.id} p={p} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
